import React, { createContext, useContext, useCallback, ReactNode } from 'react';
import { ClientOneOffPayment, ClientSubscription, EventLog, ISuperBill, ProviderService, ServiceBundle } from './models';

// Define all possible event types and their payloads
type SignalMap = {
  'oneOffPayment.created': { oneOff: ClientOneOffPayment };
  'oneOffPayment.updated': { oneOff: ClientOneOffPayment };
  'subscription.created': { sub: ClientSubscription };
  'subscription.updated': { sub: ClientSubscription };
  'superBill.created': { superBill: ISuperBill };
  'eventLog.updated': { eventLog: EventLog };
  'providerService.created': { providerService: ProviderService };
  'providerService.updated': { providerService: ProviderService };
  'serviceBundle.created': { serviceBundle: ServiceBundle };
  'serviceBundle.updated': { serviceBundle: ServiceBundle };
  'sessionCredit.created': {};
  'sessionCredit.updated': {};
};

type SignalContextType = {
  emit: <K extends keyof SignalMap>(eventName: K, data: SignalMap[K]) => void;
  signal: <K extends keyof SignalMap>(eventName: K | K[], callback: (data: SignalMap[K]) => void) => () => void;
};

export const SignalContext = createContext<SignalContextType | undefined>(undefined);

export function SignalProvider({ children }: { children: ReactNode }) {
  const listeners = React.useRef<
    Partial<{
      [K in keyof SignalMap]: Array<(data: SignalMap[K]) => void>;
    }>
  >({});

  const signal: SignalContextType['signal'] = useCallback(
    <K extends keyof SignalMap>(eventName: K | K[], callback: (data: SignalMap[K]) => void) => {
      const eventNames = Array.isArray(eventName) ? eventName : [eventName];
      const cleanupFns: Array<() => void> = [];

      eventNames.forEach((name) => {
        if (!listeners.current[name]) {
          listeners.current[name] = [];
        }
        listeners.current[name]!.push(callback);

        cleanupFns.push(() => {
          const eventListeners = listeners.current[name];
          if (eventListeners) {
            listeners.current[name] = eventListeners.filter((cb) => cb !== callback) as typeof eventListeners;
          }
        });
      });

      return () => cleanupFns.forEach((cleanup) => cleanup());
    },
    []
  );

  const emit: SignalContextType['emit'] = useCallback(<K extends keyof SignalMap>(eventName: K, data: SignalMap[K]) => {
    listeners.current[eventName]?.forEach((callback) => callback(data));
  }, []);

  const contextValue: SignalContextType = React.useMemo(() => ({ signal, emit }), [signal, emit]);

  return <SignalContext.Provider value={contextValue}>{children}</SignalContext.Provider>;
}

export const useSignals = () => {
  const context = useContext(SignalContext);
  if (!context) {
    throw new Error('useSignals must be used within a SignalProvider');
  }
  return context;
};
